/**
 * useMealScheduleAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 10 ("Meal Schedule").
 * Mirrors useFoodPreferencesAnalytics.js exactly — same GA4 wrapper via
 * react-ga4, same IS_PRODUCTION guard, same dev console.group/table
 * logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[MealScheduleAnalytics] ${eventName}`, "color:#1565C0;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useMealScheduleAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("meal_schedule_step_view", { session_id: sessionId, step: 10 });
  }, []);

  const trackTimeChange = useCallback((sessionId, field, value) => {
    send("meal_schedule_time_change", { session_id: sessionId, field, value });
  }, []);

  const trackNumMealsSelect = useCallback((sessionId, numMeals) => {
    send("meal_schedule_num_meals_select", { session_id: sessionId, num_meals: numMeals });
  }, []);

  const trackFastingWindowSelect = useCallback((sessionId, fastingWindow) => {
    send("meal_schedule_fasting_window_select", {
      session_id: sessionId,
      fasting_window: fastingWindow,
    });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("meal_schedule_step_next", {
      session_id: sessionId,
      step: 10,
      breakfast: data.breakfast,
      lunch: data.lunch,
      dinner: data.dinner,
      bedtime: data.bedtime,
      num_meals: data.numMeals,
      fasting_window: data.fastingWindow,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("meal_schedule_step_previous", { session_id: sessionId, step: 10 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("meal_schedule_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackTimeChange,
    trackNumMealsSelect,
    trackFastingWindowSelect,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useMealScheduleAnalytics;
